<ng-container>
  @if (value && preCheck) {
    @switch (attribute.type) {
      <!-- Basic types -->
      @case ('text') {
        <app-key-value icon="data-type-string"
                       [label]="getLabel() | translate"
                       [value]="value">
        </app-key-value>
      }
      @case ('number') {
        <app-key-value icon="data-type-integer"
                       [label]="getLabel() | translate">
          <div slot="custom-value" class="d-flex align-items-center">
            <ix-typography class="me-1">{{ value }}</ix-typography>
            <ix-typography>{{ getUnit() }}</ix-typography>
          </div>
        </app-key-value>
      }
      @case ('boolean') {
        <app-key-value icon="data-type-boolean"
                       [label]="getLabel() | translate">
          <ix-checkbox slot="custom-value"
                       [checked]="value"
                       [disabled]="true" />
        </app-key-value>
      }

      <!-- Special types -->
      @case ('address') {
        <app-key-value icon="home"
                       [label]="getLabel() | translate">
          <app-address slot="custom-value" [attribute]="attribute!"
                       [passport]="passport" />
        </app-key-value>
      }
      @case ('date') {
        <app-key-value icon="calendar"
                       [label]="getLabel() | translate"
                       [value]="parseDate()" />
      }
      @case ('select') {
        <app-key-value icon="data-type-enum"
                       [label]="getLabel() | translate"
                       [value]="value">
          <ix-pill slot="custom-value" variant="neutral">
            {{ parseSelect() | translate }}
          </ix-pill>
        </app-key-value>
      }
      @case ('url') {
        <app-key-value icon="globe"
                       [label]="getLabel() | translate">
          <ix-link-button slot="custom-value"
                          [url]="value"
                          target="_blank">
            {{ value }}
          </ix-link-button>
        </app-key-value>
      }
      @case ('document') {
        <app-document [attribute]="attribute"
                      [passport]="passport"
                      [schemaId]="schemaId"></app-document>
      }
      @case ('imagePicker') {
        <app-document [attribute]="attribute"
                      [passport]="passport"
                      [schemaId]="schemaId"></app-document>
      }
      @case ('email') {
        <app-key-value icon="e-mail"
                       [label]="getLabel() | translate">
          <ix-link-button slot="custom-value"
                          [url]="'mailto:' + value"
                          target="_blank">
            {{ value }}
          </ix-link-button>
        </app-key-value>
      }
      @case ('language') {
        <app-key-value icon="language"
                       [label]="getLabel() | translate">
          <app-language slot="custom-value"
                        [attributeValue]="value"
                        [valuePath]="attribute.valuePath" />
        </app-key-value>
      }
      @case ('name-id') {
        <app-key-value icon="chart-value-horizontal"
                       [label]="getLabel() | translate">
          <div slot="custom-value" class="d-flex gap-2 flex-row">
            @let nameId = getNameId();
            <ix-pill variant="neutral">
              {{ nameId.name | translate }}
            </ix-pill>
            <ix-typography>
              {{ nameId.id | translate }}
            </ix-typography>
          </div>
        </app-key-value>
      }

      <!-- Lists -->
      @case ('list') {
        <app-key-value icon="list"
                       [attribute]="attribute"
                       [label]="getLabel() | translate"
                       class="nested-list-group align-items-start">
          @for (passport of value; track passport; let i = $index) {
            <div slot="custom-value" class="nested-field-group mb-3"
            >
              <ix-key-value-list [striped]="true">
                @for (subAttribute of attribute.attributes; track subAttribute) {
                  <app-viewer-field
                    [attribute]="subAttribute"
                    [passport]="passport" [schemaId]="schemaId"
                    [valuePathPrefix]="valuePathPrefix + attribute.valuePath + '[' + i + '].'" />
                }
              </ix-key-value-list>
            </div>
          }
        </app-key-value>
      }
      @case ('list:text') {
        <app-key-value icon="list"
                       [label]="getLabel() | translate"
                       class="nested-list-group align-items-start">
          <div slot="custom-value" class="nested-field-group">
            <ix-key-value-list [striped]="true"
                               class="d-flex flex-column gap-2">
              @for (value of value; track value) {
                <ix-typography>{{ value }}</ix-typography>
              }
            </ix-key-value-list>
          </div>
        </app-key-value>
      }
      @case ('list:document') {
        <app-key-value icon="list"
                       [label]="getLabel() | translate"
                       class="nested-list-group align-items-start">
          <div slot="custom-value" class="nested-field-group">
            <ix-key-value-list [striped]="true">
              @for (passport of value; track passport) {
                <app-document [attribute]="attribute!.attributes!"
                              [passport]="passport"
                              [schemaId]="schemaId"></app-document>
              }
            </ix-key-value-list>
          </div>
        </app-key-value>
      }
      @case ('list:language') {
        <app-key-value icon="list"
                       [attribute]="attribute"
                       [label]="getLabel() | translate"
                       class="nested-list-group align-items-start">
          <div slot="custom-value" class="nested-field-group mb-3">
            <div class="language-entry-row">
              <div class="language-entry-row__badge">
                <app-language-picker #picker="appLanguagePicker"
                                     [languageEntries]="value"
                                     [fullValuePath]="fullValuePath"
                                     [attributeLabel]="getLabel() | translate" />
              </div>
              <div class="language-entry-row__content" [attr.lang]="picker.effectiveLanguage()">
                @if (findEntryByLanguage(value, picker.effectiveLanguage()); as entry) {
                  @for (selectedEntry of [entry]; track selectedEntry) {
                    <ix-key-value-list [striped]="true">
                      @for (subAttribute of attribute.attributes; track subAttribute) {
                        @if (!isLanguageColumn(subAttribute.valuePath)) {
                          <app-viewer-field
                            [attribute]="subAttribute"
                            [passport]="selectedEntry" [schemaId]="schemaId" />
                        }
                      }
                    </ix-key-value-list>
                  }
                }
              </div>
            </div>
          </div>
        </app-key-value>
      }

      <!-- Charts -->
      @case ('chart:pie') {
        <app-key-value icon="piechart"
                       [label]="getLabel() | translate"
                       class="align-items-start">
          <app-pie slot="custom-value" class="chart-container"
                   [attribute]="attribute"
                    [passport]="passport"
                    [schemaId]="schemaId" />
        </app-key-value>
      }
      @case ('chart:bar') {
        <app-key-value icon="barchart"
                       [label]="getLabel() | translate"
                       class="align-items-start">
          <app-bar slot="custom-value" class="chart-container"
                   [attribute]="attribute"
                   [passport]="passport" [schemaId]="schemaId" />
        </app-key-value>
      }
      @case ('chart:line') {
        <app-key-value icon="linechart"
                       [label]="getLabel() | translate"
                       class="align-items-start">
          <app-line slot="custom-value" class="chart-container"
                    [attribute]="attribute"
                    [passport]="passport" [schemaId]="schemaId" />
        </app-key-value>
      }

      <!-- Detail -->
      @case ('detail') {
        <app-key-value icon="details"
                       [label]="getLabel() | translate">
          <app-detail slot="custom-value"
                      [attributes]="value"
                      [label]="getLabel() | translate" />
        </app-key-value>
      }

      <!-- Undefined types -->
      @default {
        <ix-message-bar [persistent]="true"
                        type="warning">
          {{ 'battery_pass.edit.component_not_found' | translate: {type: attribute.type} }}
        </ix-message-bar>
      }
    }
  }
</ng-container>
